import { AlertOctagon, CloudOff, SatelliteDish, Truck } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '../../components/empty-state'
import { Button } from '../../components/ui/button'
import { errorMessageKey } from '../../lib/api-error'

/**
 * Empty states as documentation (spec §9) — and on this screen the distinction they
 * carry is the one the whole phase is about.
 *
 * A fleet manager opening a blank dashboard has three completely different problems,
 * and exactly one sentence each fixes:
 *
 *   no vehicles on the tenant   →  create a vehicle and pair a device. A minute's work.
 *   vehicles, no telemetry yet  →  check power, SIM, server address on the FMC150.
 *   the API did not answer      →  nothing is wrong with the fleet. Retry.
 *
 * Collapsing them into "Aucune donnée" sends somebody to the wrong place — usually to
 * the drivers, to ask why nobody worked today.
 */

export function EmptyFleetState() {
  const { t } = useTranslation()
  return (
    <EmptyState
      icon={Truck}
      title={t('dashboard:empty.noVehicles.title')}
      description={t('dashboard:empty.noVehicles.description')}
    />
  )
}

export function NoTelemetryState() {
  const { t } = useTranslation()
  return (
    <EmptyState
      icon={SatelliteDish}
      title={t('dashboard:empty.noTelemetry.title')}
      description={t('dashboard:empty.noTelemetry.description')}
    />
  )
}

/**
 * The dashboard could not be loaded at all.
 *
 * Deliberately not a grid of zeroed tiles with an error toast: a page that renders
 * "0 km" beside an error is read as a fleet that did nothing, and the toast is gone
 * four seconds later while the zeros stay on the wall display all afternoon.
 */
export function DashboardErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation()
  return (
    <EmptyState
      icon={CloudOff}
      title={t('dashboard:empty.loadFailed.title')}
      description={`${t('dashboard:empty.loadFailed.description')} ${t(errorMessageKey(error))}`}
      action={
        onRetry === undefined ? undefined : (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            {t('common:actions.retry')}
          </Button>
        )
      }
    />
  )
}

/**
 * One subsystem is down and the rest of the page is fine.
 *
 * The server already answered with `Promise.allSettled`: the affected tiles carry their
 * own pending reason and the other eight rendered normally. What that cannot do is tell
 * the reader WHICH subsystem, and "the live projection is unreachable" is the
 * difference between a support ticket and a fleet manager quietly deciding the product
 * is unreliable.
 */
export function DegradedNotice({
  degraded,
}: {
  readonly degraded: readonly { readonly key: string; readonly reason: string }[]
}) {
  const { t } = useTranslation()
  if (degraded.length === 0) return null

  return (
    <div
      role="status"
      className="mb-4 flex gap-3 rounded-lg border border-warning/40 bg-warning-subtle px-4 py-3"
    >
      <AlertOctagon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{t('dashboard:degraded.title')}</p>
        <p className="mt-0.5 text-sm text-foreground-muted">
          {t('dashboard:degraded.description')}
        </p>
        <ul className="mt-1 space-y-0.5">
          {degraded.map((entry) => (
            <li key={entry.key} className="text-xs text-foreground-subtle">
              {/* The server's own words. A translated paraphrase of "ECONNREFUSED
                  127.0.0.1:6379" is a support call that starts from zero. */}
              {entry.key} — {entry.reason}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/** A report or a chart with nothing in range. Distinct from "nothing was measured". */
export function NoRowsInRangeState({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation()
  return (
    <EmptyState
      compact={compact}
      icon={SatelliteDish}
      title={t('dashboard:empty.noRowsInRange.title')}
      description={t('dashboard:empty.noRowsInRange.description')}
    />
  )
}
